import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, CheckCircle2, XCircle, Smartphone, Tablet, Monitor, HelpCircle } from "lucide-react";

const PAGE_SIZE = 50;

interface AttemptRow {
  id: string;
  status: "success" | "failure";
  failure_reason: string | null;
  signer_name: string | null;
  signer_email: string | null;
  document_version: string | null;
  ip_address: string | null;
  user_agent: string | null;
  device_type: string | null;
  browser_name: string | null;
  browser_version: string | null;
  os_name: string | null;
  attempted_at: string;
}

function DeviceIcon({ type }: { type: string | null }) {
  switch ((type || "").toLowerCase()) {
    case "mobile": return <Smartphone className="h-3.5 w-3.5" aria-hidden />;
    case "tablet": return <Tablet className="h-3.5 w-3.5" aria-hidden />;
    case "desktop": return <Monitor className="h-3.5 w-3.5" aria-hidden />;
    default: return <HelpCircle className="h-3.5 w-3.5" aria-hidden />;
  }
}

function formatReason(r: string | null) {
  if (!r) return "";
  return r.replace(/_/g, " ");
}

/**
 * Read-only audit trail of every BAA signing attempt for the current user
 * (success and failure). Owners/managers will also see attempts from other
 * members of their agency, courtesy of the table's RLS.
 */
export function BAAAuditTrail() {
  const { user } = useAuth();
  const [rows, setRows] = useState<AttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = useCallback(async (offset: number) => {
    const { data, error } = await supabase
      .from("baa_signing_attempts")
      .select(
        "id,status,failure_reason,signer_name,signer_email,document_version,ip_address,user_agent,device_type,browser_name,browser_version,os_name,attempted_at"
      )
      .order("attempted_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);
    return { data: (data as AttemptRow[] | null) ?? [], error };
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await fetchPage(0);
      if (cancelled) return;
      if (!error) {
        setRows(data);
        setHasMore(data.length === PAGE_SIZE);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, fetchPage]);

  async function loadMore() {
    setLoadingMore(true);
    const { data, error } = await fetchPage(rows.length);
    if (!error) {
      setRows(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoadingMore(false);
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading audit trail…
      </div>
    );
  }

  if (!rows.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No signing activity recorded yet. Every attempt to sign — success or failure — will appear here.
      </p>
    );
  }

  return (
    <div className="space-y-2">
    <ScrollArea className="max-h-80 rounded-md border">
      <ul className="divide-y">
        {rows.map((r) => {
          const browser = [r.browser_name, r.browser_version].filter(Boolean).join(" ") || "Unknown browser";
          const os = r.os_name || "Unknown OS";
          const when = new Date(r.attempted_at).toLocaleString();
          return (
            <li key={r.id} className="p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {r.status === "success" ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" aria-hidden />
                  )}
                  <span className="font-medium text-foreground">
                    {r.signer_name || r.signer_email || "Unknown signer"}
                  </span>
                  <Badge
                    variant={r.status === "success" ? "secondary" : "destructive"}
                    className="capitalize"
                  >
                    {r.status}
                  </Badge>
                  {r.document_version && (
                    <Badge variant="outline">v{r.document_version}</Badge>
                  )}
                </div>
                <time className="text-xs text-muted-foreground" dateTime={r.attempted_at}>
                  {when}
                </time>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <DeviceIcon type={r.device_type} />
                  <span className="capitalize">{r.device_type || "unknown"}</span>
                </span>
                <span>· {browser} on {os}</span>
                {r.ip_address && <span>· IP {r.ip_address}</span>}
                {r.status === "failure" && r.failure_reason && (
                  <span className="text-destructive">· Reason: {formatReason(r.failure_reason)}</span>
                )}
              </div>
              {r.user_agent && (
                <p className="mt-1 truncate font-mono text-[10px] text-muted-foreground/70" title={r.user_agent}>
                  {r.user_agent}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </ScrollArea>
    {hasMore && (
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={loadMore}
        disabled={loadingMore}
      >
        {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Load more"}
      </Button>
    )}
    </div>
  );
}